import { Icon } from '@zoom/components';
import classnames from 'classnames';
import { isWeekend } from 'date-fns';
import { format, utcToZonedTime } from 'date-fns-tz';
import { TFunction } from 'i18next';
import React, { useState } from 'react';
import { Popover } from 'reactstrap';

import { useAuth } from '../../app/AuthorizationProvider';
import { NullableNumber } from '../../types';
import { BriefScheduleOverview } from './BriefScheduleOverview';
import { GanttYLabelElementProps } from './types';

const testIdSlug = 'my-schedule:weekly-view:y-label';

export const getShiftData = (
  start: NullableNumber,
  end: NullableNumber,
  t: TFunction,
  timezone: string,
  localeObj?: Locale
) =>
  start !== null && end !== null
    ? `${format(utcToZonedTime(start, timezone), t('date.formats.timeShort'), {
        locale: localeObj,
        timeZone: timezone,
      })} - ${format(utcToZonedTime(end, timezone), t('date.formats.timeShort'), {
        locale: localeObj,
        timeZone: timezone,
      })}`
    : t('layout.none');

export const GanttYLabelElement: React.FC<GanttYLabelElementProps> = ({
  date,
  dayClassName,
  startOfShift,
  endOfShift,
  isHighlighted,
  isToday,
  t,
  elements,
  settings,
  timezone,
}) => {
  const { localeObj } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const dayOfMonth = format(utcToZonedTime(date, timezone), 'd', { locale: localeObj, timeZone: timezone });
  const dayOfWeek = format(utcToZonedTime(date, timezone), 'EEEE', { timeZone: timezone });
  const dayOfWeekLocalized = format(utcToZonedTime(date, timezone), 'EEEE', { locale: localeObj, timeZone: timezone });

  const testId = `${testIdSlug}:${dayOfWeek}`;

  const className = classnames('gantt-y-label__wrapper', {
    'gantt-y-label__wrapper--active': isOpen,
    highlighted: isHighlighted,
    today: isToday,
    weekend: isWeekend(date), // TODO: Should be changed when adding support for ar-EG locale
  });

  const targetForPopover = `popover-target-${dayOfWeek}`;

  return (
    <div
      className={className}
      data-test-id={`${testId}:wrapper`}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      id={targetForPopover}
    >
      <div className="gantt-y-label__wrapper__date">
        {settings?.remote && (
          <div className="gantt-y-label__wrapper__workplace" title={t('schedule.workingRemotely')}>
            <Icon kind="house" size={15} color="green" />
          </div>
        )}
        <span className={dayClassName} data-test-id={`${testId}:day-of-month`}>
          {dayOfMonth}.
        </span>
        {dayOfWeekLocalized}
      </div>
      <div className="gantt-y-label__wrapper__shift">
        <span>{t('shiftTemplate.shift', { count: 1 })}:</span>{' '}
        <b data-test-id={`${testId}:shift`}>{getShiftData(startOfShift, endOfShift, t, timezone, localeObj)}</b>
      </div>
      {!!elements.length && (
        <Popover isOpen={isOpen} target={targetForPopover} offset="0,-8px" placement="top" fade={false}>
          <BriefScheduleOverview date={date} elements={elements} t={t} remote={settings?.remote} timezone={timezone} />
        </Popover>
      )}
    </div>
  );
};
